import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Paper,
  Grid,
  Container,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import * as caseStudyService from "../../../services/CasestudyService";
import Notification from "../../../../../components/mui/Notification";
import { Assistant } from "../../../../../components";
import debounce from "lodash/debounce";

export const IndustryDescription = ({ caseStudy, handleUpdatingStatus }) => {
  const caseStudyId = caseStudy.id;
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState();
  const [values, setValues] = useState({
    text: "",
  });

  useEffect(() => {
    caseStudyService
      .industryDescription(caseStudyId)
      .then((res) => {
        setValues({
          id: res.data?.id,
          text: res.data?.text,
          bclAssistantUsed: res.data?.createdByLLM,
        });
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message: "Could not get industry description. Please try again later",
          type: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  const updateIndustryDescription = useCallback(
    (id, name, value) => {
      handleUpdatingStatus(true);

      const request = {
        caseStudyId,
        id: id,
        [name]: value,
      };
      caseStudyService
        .updateIndustryDescriptionNew(request)
        .then((res) => {
          setValues((prevValues) => ({
            ...prevValues,
            id: res.data,
            [name]: value,
          }));
        })
        .catch((e) => {
          console.error(e);
          setNotify({
            isOpen: true,
            message:
              "Could not update industry description. Please try again later",
            type: "error",
          });
        })
        .finally(() => {
          handleUpdatingStatus(false);
        });
    },
    [handleUpdatingStatus, caseStudyId]
  );

  // Debounced save function (memoized to keep reference stable)
  const debouncedSave = useMemo(
    () =>
      debounce((id, name, value) => {
        updateIndustryDescription(id, name, value);
      }, 2000),
    [updateIndustryDescription]
  );

  const createIndustryDescription = () => {
    setCreating(true);
    handleUpdatingStatus(true);
    const request = {
      id: values.id,
      caseStudyId,
      lang: caseStudy.inputParams?.lang,
    };
    caseStudyService
      .createIndustryDescription(request)
      .then((res) => {
        setValues({
          ...values,
          id: res.data.id,
          text: res.data.text,
          bclAssistantUsed: true,
        });
      })
      .catch((e) => {
        console.error(e);
        // Detect H12 / 503 error from Heroku
        if (e.response && e.response.status === 503) {
          setNotify({
            isOpen: true,
            message:
              "Industry description creation still in progress, please retry in 10 seconds.",
            type: "warning",
          });
        } else {
          setNotify({
            isOpen: true,
            message:
              "Could not fetch industry description. Please try again later",
            type: "error",
          });
        }
      })
      .finally(() => {
        setCreating(false);
        handleUpdatingStatus(false);
      });
  };

  const handleChanges = (e) => {
    const { name, value } = e.target;
    const { id } = values;

    setValues({
      ...values,
      [name]: value,
    });

    debouncedSave(id, name, value);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <Paper variant="outlined" sx={{ my: { xs: 1 }, p: { xs: 2 } }}>
        <Grid container spacing={3} sx={{ p: { xs: 1, md: 3 } }}>
          <Grid
            container
            spacing={0}
            justify="center"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Grid item>
              <Typography sx={{ fontWeight: "bold" }} variant="h6" gutterBottom>
                Industry Description
              </Typography>
            </Grid>
            <Grid item style={{ marginLeft: "auto" }}>
              <Assistant
                disabled={creating || loading || values.bclAssistantUsed}
                onClick={createIndustryDescription}
                loading={creating}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {loading || creating ? (
              <Skeleton variant="rounded" sx={{ fontSize: "10rem" }} />
            ) : (
              <TextField
                name="text"
                multiline
                fullWidth
                value={values.text}
                onChange={handleChanges}
              />
            )}
          </Grid>
        </Grid>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Container>
  );
};
